import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Field,
  Input,
  Tab,
  TabList,
  TabValue,
  SelectTabEvent,
  SelectTabData,
  makeStyles,
} from '@fluentui/react-components';
import { ArrowUploadRegular, CheckmarkFilled, Dismiss24Regular, SearchRegular } from '@fluentui/react-icons';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { ButtonLoading, ConfirmDialog, FileDropZone, TConfirmDialogState } from '@shared/components';
import { ToastContext } from '@shared/contexts';
import { TERMINOLOGY } from '@shared/i18n';
import { toApiException } from '@shared/helpers';
import { localDateToYMD } from '@shared/utils';
import config from '@config/index';
import { useProductFolders } from '@modules/items';
import { AccessType } from '@modules/access';
import {
  filterIgnoredFiles,
  validateFileCount,
  validateFileSizes,
  validateFilenames,
  validateTotalSize,
} from '@modules/items/helpers/uploadValidation.helper';
import type { TValidationError } from '@modules/items/helpers/uploadValidation.helper';
import splitExtension from '@modules/items/helpers/splitExtension.helper';

import {
  getDuplicateLiteratureId,
  useCreateLiterature,
  useImportBulkLiteratures,
  useLookupLiterature,
  usePollBulkLiteraturesImport,
  useSaveLiterature,
} from '../../api';
import { useProductIdsSearchParam } from '../../hooks/useProductIdsSearchParam.hook';
import {
  EAssessmentDecision,
  EBulkLiteraturesImportItemStatus,
  TBulkLiteraturesImport,
  TLiterature,
  TLiteratureContent,
} from '../../types';
import AssessmentFields from '../AssessmentFields';
import ProductPicker from '../ProductPicker';
import {
  DOI_REGEX,
  PMID_REGEX,
  TLiteratureFormData,
  formToContent,
  literatureFormSchema,
} from './literatureDialogForm';
import { TBulkUploadFormData, bulkFormToAssessment, bulkUploadFormSchema } from './bulkUploadForm';
import { ManualTab } from './ManualTab';
import { buildImportResultDialog } from './buildImportResultDialog';

type TProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  literature?: TLiterature;
  onCreated?: (created: TLiterature) => void;
};

const useStyles = makeStyles({
  surface: {
    maxWidth: '640px',
    width: '100%',
  },
  surfaceWide: {
    maxWidth: '960px',
    width: '100%',
  },
  dialogContent: {
    maxHeight: '70vh',
    overflowY: 'auto',
  },
});

const LiteratureDialog = ({ isOpen, setIsOpen, literature, onCreated }: TProps) => {
  const isEditing = !!literature;
  const create = useCreateLiterature();
  const save = useSaveLiterature();
  const lookup = useLookupLiterature();

  const [selectedTab, setSelectedTab] = useState<TabValue>(isEditing ? 'manual' : 'lookup');
  const [lookupInput, setLookupInput] = useState('');
  const [lookupError, setLookupError] = useState<string | undefined>(undefined);
  const [pdfFile, setPdfFile] = useState<File | undefined>(undefined);
  const [resultDialog, setResultDialog] = useState<TConfirmDialogState | undefined>(undefined);

  const productFolders = useProductFolders();
  // BE's `checkProductsAccess` rejects non-EDIT products; mirror that here so it's visible pre-submit.
  const products = useMemo(
    () =>
      productFolders.map((item) => ({
        _id: item._id,
        name: item.name,
        disabledReason: item.accessRight < AccessType.EDIT ? 'No edit access.' : undefined,
      })),
    [productFolders]
  );

  // Drop read-only URL-seeded products so the form never starts in an unsubmittable state.
  const urlProductIds = useProductIdsSearchParam();
  const editableUrlProductIds = useMemo(() => {
    const editable = new Set(productFolders.filter((p) => p.accessRight >= AccessType.EDIT).map((p) => p._id));
    return urlProductIds.filter((id) => editable.has(id));
  }, [urlProductIds, productFolders]);

  const navigate = useNavigate();
  // The import outlives the dialog. If the user closed it and moved on, do not yank them elsewhere.
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;
  const poll = usePollBulkLiteraturesImport((job) => handleBulkImportComplete(job));
  const importBulk = useImportBulkLiteratures(poll.setJobId, poll.startProgress);

  const handleBulkImportComplete = (job: TBulkLiteraturesImport) => {
    // One file in, one record out: open it instead of leaving the user on the list.
    const [only] = job.results;
    if (
      isOpenRef.current &&
      job.results.length === 1 &&
      only.status === EBulkLiteraturesImportItemStatus.CREATED &&
      only.literatureId
    ) {
      setIsOpen(false);
      navigate(`/literature/${only.literatureId}`);
      return;
    }

    const dialogState = buildImportResultDialog({
      job,
      onDismiss: () => setResultDialog(undefined),
    });
    // Close the parent dialog unconditionally - the result dialog stands alone on top. This avoids the
    // underlying form reappearing when the user dismisses the result via ESC / X / Cancel, which don't
    // flow through onDismiss.
    setIsOpen(false);
    if (dialogState) setResultDialog(dialogState);
  };

  const { show } = useContext(ToastContext);

  // Windows with Excel installed reports a .csv as application/vnd.ms-excel, so the extension decides
  // and the mimetype is only a fallback - the same test the backend applies in isCsvImportFile.
  const isCsvFile = (file: File): boolean =>
    splitExtension(file.name)[1]?.toLowerCase() === 'csv' || file.type === 'text/csv';

  // The endpoint takes either PDFs or one CSV, never a mix, so the drop decides which set of checks runs.
  const handleImportDrop = async (files: File[]) => {
    if (files.length === 0) return;
    const { importableFiles } = filterIgnoredFiles(files);
    if (importableFiles.length === 0) {
      show('error', 'No PDF or CSV files found in the selection.');
      return;
    }
    const csvFile = importableFiles.find(isCsvFile);
    const checks: TValidationError[] = csvFile
      ? [
          importableFiles.length > 1 ? { type: 'error', message: 'Drop either PDFs or one CSV file, not both.' } : null,
          validateFileSizes([csvFile], config.MAX_FILE_SIZES_MB.LITERATURE_CSV),
        ]
      : [
          validateFileCount(importableFiles.length),
          validateFileSizes(importableFiles, config.MAX_FILE_SIZES_MB.LITERATURE_PDF),
          validateTotalSize(importableFiles, config.MAX_FILE_SIZES_MB.LITERATURE_PDF * config.MAX_FILES_UPLOAD),
          validateFilenames(importableFiles.map((f) => f.name)),
        ];
    const error = checks.find((c) => c !== null);
    if (error) {
      show(error.type, error.message);
      return;
    }
    const bulkValid = await bulkForm.trigger();
    if (!bulkValid) return;
    const assessment = bulkFormToAssessment(bulkForm.getValues());
    importBulk.mutate({ files: importableFiles, assessment });
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  const lookupInputRef = useRef<HTMLInputElement>(null);

  // Mirror backend normalizer on FE so validation runs against the bare identifier, not the pasted URL.
  const normalizeLookupInput = (raw: string): string => {
    let q = raw
      .trim()
      .replace(/^doi:/i, '')
      .replace(/^https?:\/\/(?:dx\.)?doi\.org\//i, '');
    q = q.replace(/^pmid:/i, '');
    const pubmedMatch = q.match(/^https?:\/\/(?:www\.)?pubmed\.ncbi\.nlm\.nih\.gov\/(\d+)\/?$/i);
    if (pubmedMatch) q = pubmedMatch[1];
    return q;
  };

  const isValidLookupInput = (raw: string): boolean => {
    const normalized = normalizeLookupInput(raw);
    return DOI_REGEX.test(normalized) || PMID_REGEX.test(normalized);
  };

  // Every field reset() touches must be listed here - react-hook-form latches Controllers on first mount and misses late-introduced defaults.
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<TLiteratureFormData>({
    resolver: yupResolver(literatureFormSchema),
    mode: 'onBlur',
    defaultValues: {
      type: [],
      title: '',
      authors: '',
      publicationYear: null,
      volume: '',
      issue: '',
      pages: '',
      containerTitle: '',
      publisher: '',
      doi: '',
      url: '',
      accessDate: '',
      pmid: '',
    },
  });

  // Separate form for the Upload PDF tab; keeps bulk-specific validation out of the literature form.
  const bulkForm = useForm<TBulkUploadFormData>({
    resolver: yupResolver(bulkUploadFormSchema),
    mode: 'onBlur',
    defaultValues: {
      products: [],
      decision: [EAssessmentDecision.PENDING],
      reviewingStage: [],
      exclusionReason: [],
      comment: '',
    },
  });

  useEffect(() => {
    if (!isOpen || isEditing) return;
    bulkForm.reset({
      products: editableUrlProductIds,
      decision: [EAssessmentDecision.PENDING],
      reviewingStage: [],
      exclusionReason: [],
      comment: '',
    });
  }, [isOpen, editableUrlProductIds.join(',')]);

  // draft.createdDate is stable across cache patches but changes when a new draft is created - the right discriminant to refill the form without wiping user edits.
  useEffect(() => {
    if (!isOpen) return;
    if (literature?.draft) {
      const c = literature.draft.content;
      reset({
        type: [c.type],
        title: c.title,
        authors: c.authors?.join(', ') ?? '',
        publicationYear: c.publicationYear ?? null,
        volume: c.volume ?? '',
        issue: c.issue ?? '',
        pages: c.pages ?? '',
        containerTitle: c.containerTitle ?? '',
        publisher: c.publisher ?? '',
        doi: c.doi ?? '',
        url: c.url ?? '',
        accessDate: c.accessDate?.slice(0, 10) ?? '',
        pmid: c.pmid ?? '',
      });
      setSelectedTab('manual');
    } else {
      // Fresh form for create mode; also used as a safety reset if edit is somehow opened without a draft.
      reset({ type: [], title: '' });
      setSelectedTab(isEditing ? 'manual' : 'lookup');
    }
    setPdfFile(undefined);
    setLookupInput('');
    setLookupError(undefined);
  }, [isOpen, literature?.draft?.createdDate]);

  useEffect(() => {
    if (isOpen && selectedTab === 'lookup') lookupInputRef.current?.focus();
  }, [isOpen, selectedTab]);

  const handleLookup = async () => {
    const input = normalizeLookupInput(lookupInput);
    if (!input) return;
    if (!isValidLookupInput(lookupInput)) {
      setLookupError('Enter a valid DOI (e.g. 10.1234/example) or PMID (numeric).');
      return;
    }
    setLookupError(undefined);

    try {
      const content = await lookup.mutateAsync(input);
      reset({
        type: content.type ? [content.type] : [],
        title: content.title ?? '',
        authors: content.authors?.join(', ') ?? '',
        publicationYear: content.publicationYear ?? null,
        volume: content.volume ?? '',
        issue: content.issue ?? '',
        pages: content.pages ?? '',
        containerTitle: content.containerTitle ?? '',
        publisher: content.publisher ?? '',
        doi: content.doi ?? '',
        url: content.url ?? '',
        accessDate: localDateToYMD(),
        pmid: content.pmid ?? '',
      });
      setSelectedTab('manual');
    } catch (err) {
      const existingId = getDuplicateLiteratureId(toApiException(err));
      if (existingId) {
        setLookupError(`A ${TERMINOLOGY.literature.lower} with this identifier already exists.`);
      } else {
        setLookupError('Lookup failed. Check the identifier and try again.');
      }
    }
  };

  const onSubmit = async (data: TLiteratureFormData) => {
    try {
      if (isEditing) {
        const content = formToContent(data, true);
        await save.mutateAsync({ id: literature._id, content, file: pdfFile });
      } else {
        const content = formToContent(data, false) as Partial<TLiteratureContent>;
        const created = await create.mutateAsync({ content, file: pdfFile });
        onCreated?.(created);
      }
      setIsOpen(false);
    } catch {
      // error handled by mutation onError
    }
  };

  const classes = useStyles();

  return (
    <>
      <ConfirmDialog state={resultDialog} setState={setResultDialog} />
      <Dialog open={isOpen} onOpenChange={(_, data) => setIsOpen(data.open)}>
        <DialogSurface className={selectedTab === 'manual' ? classes.surfaceWide : classes.surface}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <DialogBody>
              <DialogTitle
                action={
                  <DialogTrigger action="close">
                    <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} />
                  </DialogTrigger>
                }
              >
                {isEditing ? `Edit ${TERMINOLOGY.literature.lower}` : `Add ${TERMINOLOGY.literature.lower}`}
              </DialogTitle>
              <DialogContent className={classes.dialogContent}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) setPdfFile(file);
                    e.target.value = '';
                  }}
                />
                <TabList
                  selectedValue={selectedTab}
                  onTabSelect={(_: SelectTabEvent, data: SelectTabData) => setSelectedTab(data.value)}
                  size="small"
                  className="mb-4"
                >
                  <Tab value="lookup">Lookup</Tab>
                  <Tab value="import">Import</Tab>
                  <Tab value="manual">Manual</Tab>
                </TabList>

                {selectedTab === 'import' && (
                  <div className="space-y-3">
                    <Controller
                      name="products"
                      control={bulkForm.control}
                      render={({ field, fieldState }) => (
                        <ProductPicker
                          label="Products"
                          placeholder="Search products (optional)"
                          products={products}
                          selectedIds={(field.value as string[]) ?? []}
                          onChange={field.onChange}
                          validationMessage={fieldState.error?.message}
                        />
                      )}
                    />
                    {(bulkForm.watch('products') ?? []).length > 0 && (
                      <AssessmentFields
                        control={bulkForm.control}
                        errors={bulkForm.formState.errors}
                        watch={bulkForm.watch}
                        trigger={bulkForm.trigger}
                      />
                    )}
                    <Field hint="A PDF is read for its embedded DOI; a CSV is read for its PMID or DOI column.">
                      <FileDropZone
                        icon={<ArrowUploadRegular fontSize={32} />}
                        label="Drop PDFs, a folder, or one PubMed CSV export"
                        hint={`Up to ${config.MAX_FILES_UPLOAD} PDFs at ${config.MAX_FILE_SIZES_MB.LITERATURE_PDF} MB each, or one CSV up to ${config.MAX_FILE_SIZES_MB.LITERATURE_CSV} MB`}
                        accept="application/pdf,text/csv"
                        multiple
                        disabled={importBulk.isPending || poll.isPolling}
                        onFiles={handleImportDrop}
                      />
                    </Field>
                  </div>
                )}

                {selectedTab === 'lookup' && (
                  <div className="space-y-3">
                    <Field
                      label="DOI or PMID"
                      validationState={lookupError ? 'error' : undefined}
                      validationMessage={lookupError}
                    >
                      <Input
                        ref={lookupInputRef}
                        placeholder="e.g. 10.1234/example or 12345678"
                        value={lookupInput}
                        onChange={(_, data) => {
                          setLookupInput(data.value);
                          if (lookupError) setLookupError(undefined);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && lookupInput.trim() && !lookup.isPending) {
                            e.preventDefault();
                            handleLookup();
                          }
                        }}
                      />
                    </Field>
                    <ButtonLoading
                      appearance="primary"
                      icon={<SearchRegular />}
                      onClick={handleLookup}
                      loading={lookup.isPending}
                      disabled={!lookupInput.trim()}
                    >
                      Lookup
                    </ButtonLoading>
                  </div>
                )}

                {selectedTab === 'manual' && (
                  <ManualTab
                    control={control}
                    errors={errors}
                    pdfFile={pdfFile}
                    setPdfFile={setPdfFile}
                    fileInputRef={fileInputRef}
                  />
                )}
              </DialogContent>

              {selectedTab === 'manual' && (
                <DialogActions>
                  <ButtonLoading
                    type="submit"
                    appearance="primary"
                    icon={<CheckmarkFilled />}
                    loading={create.isPending || save.isPending}
                  >
                    {isEditing ? 'Save' : 'Create'}
                  </ButtonLoading>
                  <DialogTrigger disableButtonEnhancement>
                    <Button appearance="secondary">Cancel</Button>
                  </DialogTrigger>
                </DialogActions>
              )}
            </DialogBody>
          </form>
        </DialogSurface>
      </Dialog>
    </>
  );
};

export default LiteratureDialog;
